import type { Facing, Presence } from '../../../shared/protocol';
import { tileToScreen, type CameraState, type PlayerState } from '../firered/player';
import type { MonIcons } from '../firered/pokemon-sprites';
import type { PlayerSprites } from '../firered/sprites';

const SCREEN_WIDTH = 240;
const SCREEN_HEIGHT = 160;
const STEP_MS = 267; // 16 frames por tile andando
const FAST_STEP_MS = 133; // correndo
const BUBBLE_MS = 6000;
const BUBBLE_WIDTH_EM = 14; // largura máxima do balão, em tamanhos de fonte
const BUBBLE_MAX_LINES = 2;
const ICON_FRAME_MS = 280; // os ícones do menu alternam 2 quadros

// Índices na folha de frames (ver firered/sprites.ts)
const STAND: Record<Facing, number> = { down: 0, up: 1, left: 2, right: 2 };
const WALK: Record<Facing, [number, number]> = { down: [3, 4], up: [5, 6], left: [7, 8], right: [7, 8] };
const RUN_OFFSET = 9;
const BEHIND: Record<Facing, [number, number]> = { down: [0, -1], up: [0, 1], left: [1, 0], right: [-1, 0] };

interface Bubble {
  text: string;
  until: number;
}

interface Point {
  x: number;
  y: number;
}

/**
 * Posição de um personagem e do Pokémon que o segue, animando cada passo entre tiles.
 * O seguidor vai sempre para o tile de onde o personagem acabou de sair.
 */
class Walker {
  from: Point = { x: 0, y: 0 };
  to: Point = { x: 0, y: 0 };
  followerFrom: Point = { x: 0, y: 0 };
  followerTo: Point = { x: 0, y: 0 };
  startedAt = 0;
  duration = 0;
  steps = 0;
  map = '';

  /** Coloca no lugar (sem animação), com o seguidor atrás. */
  place(x: number, y: number, facing: Facing, map: string): void {
    const [bx, by] = BEHIND[facing];
    this.from = this.to = { x, y };
    this.followerFrom = this.followerTo = { x: x + bx, y: y + by };
    this.duration = 0;
    this.map = map;
  }

  /** Novo tile: anima a partir de onde está agora; teleporte/troca de mapa = place(). */
  moveTo(x: number, y: number, facing: Facing, map: string, fast: boolean, now: number): void {
    const current = this.position(now);
    const jump = map !== this.map || Math.abs(x - current.x) + Math.abs(y - current.y) > 1.5;
    if (jump) {
      this.place(x, y, facing, map);
      return;
    }
    if (x === this.to.x && y === this.to.y) return;
    this.followerFrom = this.followerPosition(now);
    this.followerTo = { ...this.to };
    this.from = current;
    this.to = { x, y };
    this.startedAt = now;
    this.duration = fast ? FAST_STEP_MS : STEP_MS;
    this.steps++;
  }

  progress(now: number): number {
    return this.duration ? Math.min(1, (now - this.startedAt) / this.duration) : 1;
  }

  position(now: number): Point {
    const t = this.progress(now);
    return { x: this.from.x + (this.to.x - this.from.x) * t, y: this.from.y + (this.to.y - this.from.y) * t };
  }

  followerPosition(now: number): Point {
    const t = this.progress(now);
    return {
      x: this.followerFrom.x + (this.followerTo.x - this.followerFrom.x) * t,
      y: this.followerFrom.y + (this.followerTo.y - this.followerFrom.y) * t,
    };
  }
}

interface Ghost {
  id: number;
  name: string;
  hue: number;
  pos: Presence | null;
  walker: Walker;
  bubble: Bubble | null;
}

type Drawable = { y: number; draw: () => void };

/** Cor estável por jogador (nome e balão). */
export const playerHue = (id: number): number => ((Math.abs(id) * 2654435761) >>> 0) % 360;

/**
 * Desenha por cima da tela do jogo os outros jogadores do mesmo mapa (sprite de verdade,
 * Red/Leaf), o Pokémon que segue cada um (inclusive o seu), o nick na cabeça e o balão de chat.
 */
export class GhostLayer {
  private readonly ghosts = new Map<number, Ghost>();
  private readonly ctx: CanvasRenderingContext2D;
  private selfId: number | null = null;
  private selfBubble: Bubble | null = null;
  private readonly self = new Walker();
  private icons: MonIcons | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private sprites: PlayerSprites | null,
  ) {
    this.ctx = canvas.getContext('2d')!;
  }

  setSprites(sprites: PlayerSprites | null): void {
    this.sprites = sprites;
  }

  setIcons(icons: MonIcons | null): void {
    this.icons = icons;
  }

  /** Jogador entrou (ou veio na lista inicial). */
  add(id: number, name: string, pos: Presence | null): void {
    const walker = new Walker();
    if (pos) walker.place(pos.x, pos.y, pos.facing, pos.map.join('.'));
    this.ghosts.set(id, { id, name, hue: playerHue(id), pos, walker, bubble: null });
  }

  /** Nova posição recebida do servidor. */
  move(id: number, pos: Presence, now = performance.now()): void {
    const ghost = this.ghosts.get(id);
    if (!ghost) return;
    const map = pos.map.join('.');
    if (!ghost.pos) ghost.walker.place(pos.x, pos.y, pos.facing, map);
    else ghost.walker.moveTo(pos.x, pos.y, pos.facing, map, pos.fast, now);
    ghost.pos = pos;
  }

  remove(id: number): void {
    this.ghosts.delete(id);
  }

  clear(): void {
    this.ghosts.clear();
  }

  /** Balão de fala por cima de um jogador (ou de você, se `id` for o seu). */
  say(id: number, text: string, now = performance.now()): void {
    const bubble = { text, until: now + BUBBLE_MS };
    if (id === this.selfId) this.selfBubble = bubble;
    const ghost = this.ghosts.get(id);
    if (ghost) ghost.bubble = bubble;
  }

  setSelf(id: number): void {
    this.selfId = id;
  }

  /** `view` nulo = não estamos andando no mapa (menu, batalha, título): limpa a camada. */
  render(view: { player: PlayerState; camera: CameraState } | null, now = performance.now()): void {
    const { canvas, ctx } = this;
    const width = Math.round(canvas.clientWidth * devicePixelRatio);
    const height = Math.round(canvas.clientHeight * devicePixelRatio);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    ctx.clearRect(0, 0, width, height);
    if (!view || width === 0) return;

    const scale = width / SCREEN_WIDTH;
    const { player, camera } = view;
    const map = `${player.mapGroup}.${player.mapNum}`;
    this.self.moveTo(player.x, player.y, player.facing, map, player.fast, now);
    ctx.imageSmoothingEnabled = false;

    const drawables: Drawable[] = [];
    const labels: (() => void)[] = [];

    // Você: o jogo desenha o personagem; aqui só o seguidor, o nick e o balão.
    this.addFollower(drawables, camera, this.self, player.lead, now, scale);
    // A câmera segue você: o personagem fica no centro da tela.
    const selfLeft = 112 - camera.panX;
    const selfTop = 56 - (camera.panY - 32);
    labels.push(() =>
      this.drawLabel(player.nick, this.selfId === null ? 45 : playerHue(this.selfId), selfLeft, selfTop, scale, '', this.selfBubble, now),
    );

    for (const ghost of this.ghosts.values()) {
      const pos = ghost.pos;
      if (!pos || pos.map[0] !== player.mapGroup || pos.map[1] !== player.mapNum) continue;
      const { x, y } = ghost.walker.position(now);
      const { left, top } = tileToScreen(camera, x, y);
      if (left < -48 || top < -64 || left > SCREEN_WIDTH + 32 || top > SCREEN_HEIGHT + 32) continue;
      this.addFollower(drawables, camera, ghost.walker, pos.lead, now, scale);
      const progress = ghost.walker.progress(now);
      drawables.push({ y: top, draw: () => this.drawSprite(ghost, left, top, progress, scale) });
      const battle = pos.scene === 'battle' ? '⚔️ ' : '';
      labels.push(() => this.drawLabel(pos.nick || ghost.name, ghost.hue, left, top, scale, battle, ghost.bubble, now));
    }

    // Quem está mais embaixo na tela é desenhado por cima; nomes e balões por último.
    drawables.sort((a, b) => a.y - b.y).forEach((d) => d.draw());
    labels.forEach((draw) => draw());
  }

  private addFollower(drawables: Drawable[], camera: CameraState, walker: Walker, species: number, now: number, scale: number): void {
    const frames = species ? this.icons?.get(species) : null;
    if (!frames) return;
    const { x, y } = walker.followerPosition(now);
    const { left, top } = tileToScreen(camera, x, y);
    // Ícone 32x32 centralizado no tile, com a base no chão do tile.
    const iconLeft = left - 8;
    const iconTop = top + 2;
    const frame = frames[Math.floor(now / ICON_FRAME_MS) % 2];
    drawables.push({
      y: top,
      draw: () => {
        const { ctx } = this;
        ctx.save();
        ctx.globalAlpha = 0.95;
        ctx.drawImage(frame, Math.round(iconLeft * scale), Math.round(iconTop * scale), Math.round(32 * scale), Math.round(32 * scale));
        ctx.restore();
      },
    });
  }

  private labelFont(scale: number): number {
    return Math.max(10, Math.round(6.5 * scale));
  }

  private drawSprite(ghost: Ghost, left: number, top: number, progress: number, scale: number): void {
    if (!this.sprites) return;
    const pos = ghost.pos!;
    const frames = this.sprites.byGender[pos.gender];
    const walking = progress < 1;
    // Meio passo com o pé à frente (alternando os pés), meio passo parado.
    let frame = walking && progress < 0.5 ? WALK[pos.facing][ghost.walker.steps % 2] : STAND[pos.facing];
    if (pos.fast && walking) frame += RUN_OFFSET;

    const { ctx } = this;
    ctx.save();
    ctx.globalAlpha = 0.95;
    const x = Math.round(left * scale);
    const y = Math.round(top * scale);
    const w = Math.round(16 * scale);
    const h = Math.round(32 * scale);
    if (pos.facing === 'right') {
      ctx.translate(x + w, y);
      ctx.scale(-1, 1);
      ctx.drawImage(frames[frame], 0, 0, w, h);
    } else {
      ctx.drawImage(frames[frame], x, y, w, h);
    }
    ctx.restore();
  }

  /** Nick em cima do personagem (canto superior esquerdo do sprite 16x32 em `left`,`top`). */
  private drawLabel(name: string, hue: number, left: number, top: number, scale: number, prefix: string, bubble: Bubble | null, now: number): void {
    if (!name) return;
    const { ctx } = this;
    const centerX = (left + 8) * scale;
    const fontSize = this.labelFont(scale);
    ctx.font = `700 ${fontSize}px system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const label = `${prefix}${name}`;
    const labelY = (top + 6) * scale - fontSize * 0.6;
    const labelWidth = ctx.measureText(label).width + fontSize * 0.8;
    ctx.fillStyle = 'rgba(10, 10, 11, 0.72)';
    roundRect(ctx, centerX - labelWidth / 2, labelY - fontSize * 0.7, labelWidth, fontSize * 1.4, fontSize * 0.5);
    ctx.fill();
    ctx.fillStyle = `hsl(${hue} 90% 80%)`;
    ctx.fillText(label, centerX, labelY);

    if (bubble && bubble.until > now) this.drawBubble(bubble, centerX, labelY, fontSize, now);
  }

  /** Balão acima da etiqueta de nome (`labelY` = centro da etiqueta), com até 2 linhas. */
  private drawBubble(bubble: Bubble, centerX: number, labelY: number, fontSize: number, now: number): void {
    const { ctx } = this;
    ctx.save();
    ctx.font = `600 ${fontSize}px system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.globalAlpha = Math.min(1, (bubble.until - now) / 600);

    const padding = fontSize * 0.5;
    const maxTextWidth = Math.min(fontSize * BUBBLE_WIDTH_EM, ctx.canvas.width - 8 - padding * 2);
    const lines = wrap(ctx, bubble.text, maxTextWidth, BUBBLE_MAX_LINES);
    const lineHeight = fontSize * 1.2;
    const width = Math.max(...lines.map((line) => ctx.measureText(line).width)) + padding * 2;
    const height = lines.length * lineHeight + padding;
    const bottom = labelY - fontSize * 1.05;
    const left = Math.max(4, Math.min(centerX - width / 2, ctx.canvas.width - width - 4));

    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    roundRect(ctx, left, bottom - height, width, height, fontSize * 0.6);
    ctx.fill();
    ctx.fillStyle = '#16140f';
    lines.forEach((line, i) => {
      ctx.fillText(line, left + width / 2, bottom - height + padding / 2 + lineHeight * (i + 0.5));
    });
    ctx.restore();
  }
}

/** Quebra o texto em linhas que cabem em `maxWidth`; corta com "…" se passar de `maxLines`. */
function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !line) {
      line = candidate;
      continue;
    }
    lines.push(line);
    line = word;
  }
  if (line) lines.push(line);

  const shown = lines.slice(0, maxLines);
  const truncated = lines.length > maxLines;
  const last = shown.length - 1;
  if (truncated || ctx.measureText(shown[last]).width > maxWidth) {
    let tail = shown[last];
    while (tail && ctx.measureText(`${tail}…`).width > maxWidth) tail = tail.slice(0, -1);
    shown[last] = `${tail.trimEnd()}…`;
  }
  return shown;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}
